import { geohash2point } from '../utils.js'

// Status color mapping
const STATUS_COLORS = {
  available: '#308f00', // green
  busyavailable: '#e08e00', // orange
  policeaction: '#FFFFFF', // white
  incident: '#FFFFFF', // white
  securityoperation: '#4d88ff', // blue
  default: '#808080', // gray - OFFLINE
  no_signal: '#606060', // dark gray - NO SIGNAL
}

// Default offline timeout in ms
const DEFAULT_OFFLINE_TIMEOUT = 200_000

/**
 * UnitRenderer - Renders and manages unit markers on the map
 *
 * Handles rendering of units (vehicles, personnel) with status indicators,
 * heading arrows, labels, and popups.
 */
export default class UnitRenderer {
  /**
   * Create a new UnitRenderer
   * @param {ItemManager} itemManager - The ItemManager instance
   * @param {LayerManager} layerManager - The LayerManager instance
   * @param {Object} [options={}] - Configuration options
   * @param {Function} [options.warnFn] - Optional warning function for logging
   * @param {Function} [options.onUnitSelected] - Callback when unit is clicked
   */
  constructor(itemManager, layerManager, options = {}) {
    this._itemManager = itemManager
    this._layerManager = layerManager
    this._warn = options.warnFn || console.warn
    this._onUnitSelected = options.onUnitSelected || null
  }

  /**
   * Set the unit selection callback
   * @param {Function} callback - Callback function(unitId)
   */
  setUnitSelectedCallback(callback) {
    this._onUnitSelected = callback
  }

  /**
   * Toggle visibility of the units layer
   *
   * @param {boolean} visible - True to show units, false to hide
   */
  toggleUnitsLayer(visible) {
    const layerId = 'units'
    if (!this._layerManager.hasLayer(layerId)) {
      this._layerManager.setLayer(layerId, { zindex: 700, toggleable: true, visible })
    } else {
      this._layerManager.setLayerVisibility(layerId, visible)
    }
  }

  /**
   * Update the "myself" marker (current user position)
   *
   * @param {Object} myself - Position data
   * @param {string} myself.gps - GPS position as geohash
   * @param {number} [myself.heading] - Direction of travel in degrees
   */
  updateMyself(myself) {
    const { gps, heading } = myself
    const layerId = 'myself'

    if (!this._layerManager.hasLayer(layerId)) {
      this._layerManager.setLayer(layerId, { zindex: 900, toggleable: false, visible: true })
    }

    if (!gps) return

    const coords = geohash2point(gps)

    const iconSize = 48
    const iconHtml = `
      <svg id="marker.gps" viewBox="-1 -1 32 32">
        <polygon points="15 0.5 3.5 28 6 29.5 15 26.5 24 29.5 26.5 28 15 0.5" fill="gold" stroke="black" class="rounded" transform="rotate(${heading || 0}, 15, 15)"/>
      </svg>
    `

    this._itemManager.addItem({
      id: 'myself',
      layer: layerId,
      geometry: {
        type: 'Point',
        coordinates: coords,
      },
      icon: {
        html: iconHtml,
        size: [iconSize, iconSize],
        anchor: [iconSize / 2, iconSize / 2],
        classname: 'myself-marker',
      },
    })
  }

  /**
   * Get status color for a unit
   * @private
   */
  _getStatusColor(status, noSignal) {
    if (noSignal) {
      return STATUS_COLORS.no_signal
    }

    const statusLower = status?.toLowerCase()
    return STATUS_COLORS[statusLower] || STATUS_COLORS.default
  }

  /**
   * Update or create a unit marker on the map
   *
   * @param {Object} unit - Unit data
   * @param {string} unit.id - Unique unit identifier
   * @param {string} unit.gps - GPS position as geohash
   * @param {number} [unit.heading] - Direction of travel in degrees
   * @param {string} [unit.status] - Unit status
   * @param {string} [unit.label] - Display label
   * @param {string} [unit.description] - Additional description
   * @param {boolean} [unit.lightbar] - Emergency lights active
   * @param {string} [unit.gps_dt] - Timestamp of last GPS update
   * @param {boolean} [unit.no_signal] - True if unit is offline
   * @param {string} [unit.user] - User name
   */
  updateUnit(unit) {
    const layerId = 'units'
    if (!this._layerManager.hasLayer(layerId)) return

    const { id, gps, heading, status, label, description, lightbar, gps_dt, no_signal, user } = unit

    if (!id || !gps) {
      this._warn('Unit must have id and gps:', unit)
      return
    }

    const coords = geohash2point(gps)
    const color = this._getStatusColor(status, no_signal)
    const headingAngle = heading || 0
    const toggleLightbar = Boolean(lightbar)

    // Create SVG circle with label
    const circleSize = 80
    const iconHtml = `
      <svg width="${circleSize}" height="${circleSize}" viewBox="0 0 200 200">
        <g filter="url(#filter0_d_11_2)">
          <circle class="${toggleLightbar ? 'lightbar' : ''}" cx="100.143" cy="100.143" r="68.1435" fill="${color}" fill-opacity="0.3" stroke="white" stroke-width="8"/>
          <circle cx="100.143" cy="99.086" r="29.8711" fill="${color}"/>
        </g>
        <path transform="rotate(${headingAngle},100.143,100.143)" d="M101 1.97626L128.309 30.5806C128.309 30.5806 116.361 23.9844 99.9262 23.9844C83.4918 23.9845 71.9477 30.5807 71.9477 30.5807L101 1.97626Z" fill="black"/>
        <defs>
          <filter id="filter0_d_11_2" x="22" y="22" width="176.287" height="176.287" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_11_2"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_11_2" result="shape"/>
          </filter>
        </defs>
      </svg>
      ${label ? `<div style="
        position: absolute;
        bottom: -5px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 2px 8px;
        border-radius: 4px;
        white-space: nowrap;
        font-size: 12px;
        font-weight: bold;
      ">${label}</div>` : ''}
    `

    const self = this
    this._itemManager.addItem({
      id,
      layer: layerId,
      geometry: {
        type: 'Point',
        coordinates: coords,
      },
      icon: {
        html: iconHtml,
        size: [circleSize, circleSize + 20],
        anchor: [circleSize / 2, circleSize / 2],
        classname: 'unit-marker',
      },
      popup: {
        content: `
          <div class="device-popup">
            <div class="device-popup__header">
              <strong>${id}</strong>
              <span>${status}</span>
            </div>
            <div class="device-popup__content">
              ${no_signal ? `
                <div class="device-popup__row">
                  <strong>Strata signálu:</strong>
                  <span>${new Date(gps_dt).toLocaleString('sk-SK')}</span>
                </div>
              ` : ''}
              ${user ? `
                <div class="device-popup__row">
                  <strong>Meno:</strong>
                  <span>${user}</span>
                </div>
              ` : ''}
              ${description ? `
                <div class="device-popup__description">
                  ${description}
                </div>
              ` : ''}
            </div>
          </div>
        `,
      },
      onClick: (unitId) => {
        if (typeof self._onUnitSelected === 'function') {
          self._onUnitSelected(unitId)
        }
      },
    })
  }

  /**
   * Render or update a unit marker from raw server data
   *
   * @param {Object} unit - Unit data from server
   * @param {number} [timeout=200000] - Offline timeout in ms
   */
  renderUnit(unit, timeout = DEFAULT_OFFLINE_TIMEOUT) {
    const { id, gps, heading, status, callsign, lightbar, siren, gps_dt, description, role, firstname, lastname, login } = unit

    const label = `${callsign || id}${role ? ' - ' + role : ''}`
    const light = lightbar || siren
    const no_signal = gps_dt ? (Date.now() - new Date(gps_dt).getTime() > timeout) : true

    this.updateUnit({
      id,
      gps,
      heading,
      status,
      label,
      description,
      lightbar: light,
      gps_dt,
      user: lastname ? `${lastname}, ${firstname}` : login,
      no_signal,
    })
  }
}
